import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { businessInfo } from '../mockData';
import { siteConfig, rates, fixedQuoteLine } from '../content/siteContent';
import usePageMeta from '../hooks/usePageMeta';

const examples = [
  { job: 'A standard house or business lockout', price: 'usually $120–$180' },
  { job: 'Rekeying', price: `from $${rates.rekeyPerLockFrom} per lock + call-out` },
  { job: 'Key cutting (on an existing visit)', price: `$${rates.keyCutFrom}–$${rates.keyCutTo} per standard key` },
  { job: 'Lock repairs, replacements & security upgrades', price: 'call-out + time, fixed-quoted first' },
];

const Pricing = () => {
  usePageMeta();
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gray-950 py-16 sm:py-20 text-white overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-full md:w-3/5" aria-hidden="true">
          <img src="/images/stock/pricing-handover.jpg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-950 via-gray-950/50 to-gray-950/5"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-5">Simple, upfront pricing</h1>
            <p className="text-xl text-gray-300 mb-8">
              Clear call-out pricing and a fixed quote before any extra work starts — no surprises,
              no hidden fees. Mobile across Auckland's North Shore.
            </p>
            <a href={`tel:${businessInfo.phoneTel}`}>
              <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold text-lg px-8">
                <Phone className="mr-2" size={20} />
                Call {businessInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Rate card */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Residential call-out rates</h2>
              <ul className="space-y-4">
                <li className="flex justify-between items-start gap-6 border-b border-red-100 pb-4">
                  <div>
                    <p className="font-semibold text-gray-900">Call-out</p>
                    <p className="text-sm text-gray-600">Includes travel + the first 15 minutes on site</p>
                  </div>
                  <span className="text-2xl font-bold text-gray-900 whitespace-nowrap">${rates.callout}</span>
                </li>
                <li className="flex justify-between items-start gap-6 border-b border-red-100 pb-4">
                  <div>
                    <p className="font-semibold text-gray-900">Each additional 15 minutes</p>
                    <p className="text-sm text-gray-600">Only if the job runs beyond the first 15 minutes</p>
                  </div>
                  <span className="text-2xl font-bold text-gray-900 whitespace-nowrap">${rates.block15}</span>
                </li>
              </ul>
              <p className="text-sm text-gray-600 mt-4">All prices include GST.</p>
              <p className="text-base font-semibold text-red-800 mt-4 flex items-start gap-2">
                <CheckCircle className="text-red-600 flex-shrink-0 mt-0.5" size={18} />
                {fixedQuoteLine}
              </p>
              <p className="text-sm text-gray-700 mt-3">
                That call-out is a genuine price — not a lowball fee that balloons on the invoice. What we quote, you pay.
              </p>
            </CardContent>
          </Card>

          {/* Examples */}
          <h2 className="text-2xl font-bold text-gray-900 mt-14 mb-6 text-center">What a typical job costs</h2>
          <div className="space-y-3">
            {examples.map((e) => (
              <div key={e.job} className="flex justify-between items-center gap-6 bg-gray-50 border border-gray-200 rounded-lg px-5 py-4">
                <span className="text-gray-800">{e.job}</span>
                <span className="font-semibold text-gray-900 text-right whitespace-nowrap">{e.price}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-600 text-sm mt-5 text-center">
            Trickier jobs — a lock that has to be drilled, damaged hardware or a broken key — cost more. We'll always confirm the full quote on site before any work starts.
          </p>

          {/* Commercial note */}
          <Card className="border-gray-200 mt-12">
            <CardContent className="p-6 sm:p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Commercial & trade work</h3>
              <p className="text-gray-700 mb-4">
                Commercial door hardware, access control, master key systems and trade-partner
                installs are quoted per job — tell us the scope and we'll come back with a clear,
                itemised quote.
              </p>
              <Link to="/contact">
                <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                  Request a commercial quote
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Need a locksmith now?</h2>
          <p className="text-lg text-gray-300 mb-7">{siteConfig.hoursLine} · Mobile across the North Shore — we come to you.</p>
          <a href={`tel:${businessInfo.phoneTel}`}>
            <Button size="lg" className="bg-red-600 text-white hover:bg-red-700 font-bold">
              <Phone className="mr-2" size={20} />
              Call {businessInfo.phone}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
